// ─────────────────────────────────────────────────────────────────────────────
// CMS Reports API — all endpoints live:
//   report/cms (list) · booksectioncrop/deleteinfo (precheck) ·
//   booksectioncrop/delete (delete) · report/update (mark-done)
// ─────────────────────────────────────────────────────────────────────────────

import { ApiUrl } from "./common/Config";
import { get, post } from "./common/Helper";
import { deleteQuestion } from "./BookSectionCrop";

/**
 * List every error-reported question.  POST report/cms
 *   payload: { StatusType, MonthCount, OrderBy }
 *     StatusType : 0 = only unresolved/open reports   (Feedbacks uses 2 = all)
 *     MonthCount : how many months back to include
 *     OrderBy    : 0 = by date, 1 = by report count
 *   Returns: { status:200, data: { ResponseStatus, ResponseData: [...rows], ResponseMessage } }
 *
 * Row shape: { TotalReportCount, FilteredCount, MaxDate, ReportId,
 *              BookSectionCropId, BookSectionId, QuestionNumber,
 *              AchievementVideoId, LectureSectionId, Status, ReportContentType }
 */
export const getCMSReports = async (params, token) => {
  const url = ApiUrl + "report/cms";
  const payload = {
    StatusType: params?.StatusType ?? 0,
    MonthCount: params?.MonthCount ?? 1,
    OrderBy: params?.OrderBy ?? 1,
  };
  return await post(url, payload, token);
};

/**
 * Pre-delete safety check.  GET booksectioncrop/deleteinfo?booksectioncropid=<id>
 * Does NOT delete — only tells the UI which modal to show.
 *
 * Returns: { status:200, data: { responseStatus, responseMessage, responseData } }
 *   responseData is null on authorization failure; otherwise:
 *   {
 *     canDelete: boolean,
 *     type: "Error" | "Warning" | "Info" | "Success",
 *     message: string,
 *     achievementInfos: [{ achievementId, currentQuestionCount, remainingQuestionCount, displayText }],
 *     usedAreas: [{ type, id, message }]
 *   }
 *   (NOTE: this endpoint's body is camelCase, unlike report/cms which is PascalCase.)
 */
export const getDeleteInfo = async (bookSectionCropId, token) => {
  const url =
    ApiUrl + "booksectioncrop/deleteinfo?booksectioncropid=" + bookSectionCropId;
  return await get(url, token);
};

/**
 * Actually delete the question (runs only after getDeleteInfo says canDelete).
 * Reuses Feedback's shared DEV delete endpoint (booksectioncrop/delete?bookSectionCropId=<id>);
 * these questions don't exist in PROD, so no prod delete is needed.
 */
export const deleteQuestionCMS = async (bookSectionCropId, token) => {
  return await deleteQuestion(bookSectionCropId, token);
};

/**
 * Mark a reported question as resolved/done.  POST report/update
 *   payload: { Id: <ReportId>, ReportContentType: 0 }
 */
export const markReportAsDone = async (reportId, token) => {
  const url = ApiUrl + "report/update";
  return await post(url, { Id: reportId, ReportContentType: 5 }, token);
};

export const getCMSReportDetails = async (bookSectionCropId, token) => {
  const url = ApiUrl + "report/cmsdetails";
  return await post(url, { BookSectionCropId: bookSectionCropId }, token);
};
